// Copyright (c) Bentley Systems
import { Button, Fieldset, ProgressRadial, Text } from "@itwin/itwinui-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { EformViewModel } from "./ViewModels/eform-view-model";
import { RecordItem } from "./ViewModels/record-vm";
import { EformStructureModel } from "./DataModels/eform-structure-model";
import Action from "../Action/Action";
import {
  getJsonFromParameters,
  getParametersFromUrlString,
  getQueryParam,
  getTranslatedStringFromParameters,
  getUrlBasedOnEnv,
  setErrorState,
} from "../Helpers/helper-functions";
import { selectAccessToken } from "../app/authorization-slice";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import { ActionParameterVm } from "../Action/action-parameter-vm";
import Record from "./Record";
import {
  selectInvokeChange,
  setFormChanged,
  setFormValid,
  setNewFormAllValidNoChange,
} from "../app/form-changed-slice";
import { clearIsError, ErrorState, selectIsError } from "../app/is-error-slice";

export type EformProps = {
  id?: string | undefined;
  params?: string | undefined;
  ///is open directly from URL or is it open in InformationPanel in elist
  isFromUrl: boolean;
  closeFrollFunction?: (onFrollClose: boolean) => void;
  navigateToInformationPanelFunction?: (navFrolUrl: string) => void;
  setInforPanelHeaderFunction?: (header: string) => void;
  refreshInRollOutWindowProperty?: boolean;
  wasEformChangedAndRefreshed?: boolean;
};

const Eform = (props: EformProps) => {
  const [viewModel, setViewModel] = useState<EformViewModel>();
  const [isLoading, setLoading] = useState<boolean>(true);
  const [actionCount, setActionCount] = useState<number>(0);
  const [
    wasActionEndedReloadedFormWasChanged,
    setWasActionEndedReloadedFormWasChanged,
  ] = useState<boolean>(false);
  const [hasDataFetchError, setHasDataFetchError] = useState<boolean>(false);

  const abortControllerRef = useRef<AbortController | null>(null);

  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const { id, params } = useParams();
  const urlParameters = useMemo(
    () => getParametersFromUrlString(params),
    [params],
  );
  const iTwinQueryParam = getQueryParam("iTwinId");
  const formId = props && props.isFromUrl ? id : props.id;
  const parameters =
    props && props.isFromUrl
      ? urlParameters
      : getParametersFromUrlString(props.params);

  const accessToken = useAppSelector(selectAccessToken);
  const invokeFormChanged = useAppSelector(selectInvokeChange);
  const errorState = useAppSelector(selectIsError);

  function setInfoPanelHeader(vmHeader: string | undefined) {
    if (
      props.setInforPanelHeaderFunction !== undefined &&
      vmHeader !== undefined
    ) {
      const header = getTranslatedStringFromParameters(vmHeader, parameters);
      props.setInforPanelHeaderFunction(header as string);
    }
  }

  function setFormState(
    vm: EformViewModel | undefined,
    fromFirstLoadForNewForm?: boolean,
  ) {
    if (!vm) {
      return;
    }

    const isChanged = setIsFormChanged(vm);
    if (!isChanged) {
      dispatch(setNewFormAllValidNoChange(false));
      return;
    }

    if (fromFirstLoadForNewForm) {
      dispatch(setNewFormAllValidNoChange(true));
      return;
    }

    const isValid = setIsFormValid(vm);
    dispatch(setNewFormAllValidNoChange(isValid));
  }

  function setIsFormValid(vm: EformViewModel): boolean {
    if (vm.isFormValid) {
      dispatch(setFormValid(true));
      return true;
    } else {
      dispatch(setFormValid(false));
      return false;
    }
  }

  function setIsFormChanged(vm: EformViewModel): boolean {
    if (
      vm.isFormChanged ||
      props.wasEformChangedAndRefreshed === true ||
      wasActionEndedReloadedFormWasChanged === true
    ) {
      dispatch(setFormChanged(true));
      return true;
    } else {
      dispatch(setFormChanged(false));
      return false;
    }
  }

  useEffect(() => {
    abortControllerRef.current = new AbortController();
    dispatch(setFormValid(false));
    dispatch(setFormChanged(false));
    dispatch(setNewFormAllValidNoChange(false));
    setLoading(true);
    setViewModel(undefined);
    setHasDataFetchError(false);
    let structureModel: EformStructureModel;
    if (formId !== undefined) {
      const url = getUrlBasedOnEnv(
        `/api/Structure/GetNodeConfiguration/${encodeURIComponent(formId ?? "")}`,
      );
      const jsonBody = getJsonFromParameters(parameters);
      const runIdStructure = crypto.randomUUID();
      fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: accessToken,
          RunId: runIdStructure,
          ...(iTwinQueryParam && { "Itwin-ID": iTwinQueryParam }),
        },
        body: jsonBody,
        signal: abortControllerRef.current.signal,
      })
        .then(async function (response) {
          if (!response.ok) {
            setErrorState(response.status, dispatch, runIdStructure, true);
            setHasDataFetchError(true);
          } else {
            return response.json();
          }
        })
        .then(function (data) {
          if (!data) {
            return;
          }
          structureModel = new EformStructureModel(data);
          const efViewModel = new EformViewModel(structureModel, parameters);
          if (efViewModel?.keyParameter !== undefined) {
            const url2 = getUrlBasedOnEnv(
              `/api/Data/GetItemData/${encodeURIComponent(
                formId ?? "",
              )}/${encodeURIComponent(String(efViewModel.keyParameter.value))}`,
            );
            const runIdData = crypto.randomUUID();
            void fetch(url2, {
              method: "GET",
              headers: {
                Authorization: accessToken,
                RunId: runIdData,
                ...(iTwinQueryParam && { "Itwin-ID": iTwinQueryParam }),
              },
              signal: abortControllerRef.current?.signal,
            })
              .then(async function (response2) {
                if (!response2.ok) {
                  setErrorState(response2.status, dispatch, runIdData, true);
                  setHasDataFetchError(true);
                } else {
                  return response2.json();
                }
              })
              .then((data2) => {
                if (data2) {
                  efViewModel.setRecordsData(data2.records);
                  setViewModel(efViewModel);
                  setInfoPanelHeader(efViewModel.header);
                  // Clear error state on successful data load
                  setHasDataFetchError(false);
                  if (errorState.errorState !== ErrorState.NoError) {
                    dispatch(clearIsError());
                  }
                }
                setFormState(efViewModel, false);
              })
              .finally(() => setLoading(false));
          } else {
            efViewModel.setRecordsData();
            setViewModel(efViewModel);
            setInfoPanelHeader(efViewModel.header);
            // Clear error state on successful data load
            setHasDataFetchError(false);
            if (errorState.errorState !== ErrorState.NoError) {
              dispatch(clearIsError());
            }
            setLoading(false);
            setFormState(efViewModel, true);
          }
        })
        .catch((error: any) => {
          if (error?.name !== "AbortError") {
            setErrorState(500, dispatch, runIdStructure, true);
            setHasDataFetchError(true);
          }
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [
    id,
    urlParameters,
    actionCount,
    props.id,
    props.refreshInRollOutWindowProperty,
    props.params,
    dispatch,
  ]);

  const eformSaveFunction = async (actionId: number): Promise<boolean> => {
    const saveSuccessfulyCompleted = await callSave(actionId);
    return saveSuccessfulyCompleted;
  };

  useEffect(() => {
    setFormState(viewModel);
  }, [invokeFormChanged]);

  async function callSave(actionId: number): Promise<boolean> {
    let url = "";
    if (viewModel?.keyParameter === undefined) {
      url = getUrlBasedOnEnv(
        `/api/Data/SaveItemData/${encodeURIComponent(
          formId ?? "",
        )}/${encodeURIComponent(actionId.toString())}`,
      );
    } else {
      url = getUrlBasedOnEnv(
        `/api/Data/SaveItemData/${encodeURIComponent(
          formId ?? "",
        )}/${encodeURIComponent(actionId.toString())}/${encodeURIComponent(
          String(viewModel.keyParameter.value),
        )}`,
      );
    }
    const records: any[] = [];
    viewModel?.records.forEach((record) => {
      const recordItemsObjects: any[] = [];

      record.recordItems.forEach((recordItem) => {
        const fieldsObjects: any[] = [];
        recordItem.fields.forEach((field) => {
          const fobj = {
            name: field.name,
            value:
              field.selectedValue === undefined || field.selectedValue === null
                ? null
                : field.selectedValue.toString(),
          };
          fieldsObjects.push(fobj);
        });
        const recordItemObject = {
          recordValues: fieldsObjects,
        };
        recordItemsObjects.push(recordItemObject);
      });
      const recordObj = {
        id: record.recordId,
        recordItems: recordItemsObjects,
      };
      records.push(recordObj);
    });

    const body = { records };
    const jsonBody = JSON.stringify(body);
    const saveRunId = crypto.randomUUID();
    let result = true;
    await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: accessToken,
        RunId: saveRunId,
        ...(iTwinQueryParam && { "Itwin-ID": iTwinQueryParam }),
      },
      body: jsonBody,
    })
      .then(async function (response) {
        if (!response.ok) {
          setErrorState(response.status, dispatch, saveRunId, true);
        } else {
          return response.json();
        }
      })
      .then(async (data) => {
        if (!data) {
          result = false;
        }
      });

    return result;
  }

  function actionEnded() {
    setActionCount(actionCount + 1);
    setWasActionEndedReloadedFormWasChanged(true);
  }

  function getHeaderBackAction(): React.JSX.Element | undefined {
    if (viewModel?.headerBackAction !== undefined) {
      return (
        <Action
          action={viewModel.headerBackAction}
          saveFunction={eformSaveFunction}
          key={viewModel.headerBackAction.actionId}
          setActionParametersCurrentFieldsValue={setActionParamsCurrentValues}
          closeFrollFunction={props.closeFrollFunction}
        />
      );
    }
  }

  const headerButtons = viewModel?.visibleActionsUp.map((action) => (
    <Action
      action={action}
      saveFunction={eformSaveFunction}
      key={action.actionId}
      setActionParametersCurrentFieldsValue={setActionParamsCurrentValues}
      closeFrollFunction={props.closeFrollFunction}
    />
  ));

  const footerButtons = viewModel?.visibleActionsDown.map((action) => (
    <Action
      action={action}
      saveFunction={eformSaveFunction}
      key={action.actionId}
      setActionParametersCurrentFieldsValue={setActionParamsCurrentValues}
      closeFrollFunction={props.closeFrollFunction}
    />
  ));

  function setActionParamsCurrentValues(actionParameters: ActionParameterVm[]) {
    actionParameters.forEach((p) => {
      if (p.valueType === "F") {
        if (viewModel !== undefined) {
          let isFound = false;
          for (const record of viewModel.records) {
            for (const recordItem of record.recordItems) {
              const field = recordItem.fields.find((f) => f.name === p.name);
              if (field !== undefined) {
                p.value = field.selectedValue;
                isFound = true;
                break;
              }
            }
            if (isFound === true) {
              break;
            }
          }
        }
      }
    });
  }

  function getRecords(): React.JSX.Element[] | undefined {
    const returnValue: React.JSX.Element[] = [];
    viewModel?.records.forEach((record, index) => {
      const keyString = `record${(
        record.recordId as number
      ).toString()}_${index}`;
      returnValue.push(
        index > 0 ? (
          <Fieldset
            key={keyString}
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 16,
              marginTop: "0.5rem",
              border: 0,
              padding: 0,
            }}
          >
            {
              <Record
                key={keyString}
                showAddButton={true}
                recordItems={record.recordItems}
                recordId={record.recordId as number}
                eformId={formId as unknown as number}
                allMatchCodes={viewModel?.matchCodes}
                efieldOptions={viewModel?.efieldOptions}
                recordAddedFunction={(newRecordItem: RecordItem) => {
                  record.recordItems.push(newRecordItem);
                }}
                recordRemovedFunction={(recordItemToRemove: RecordItem) => {
                  const removeIndex =
                    record.recordItems.indexOf(recordItemToRemove);
                  if (removeIndex !== -1) {
                    record.recordItems.splice(removeIndex, 1);
                  }
                }}
                callActionFunction={callAutoStartAction}
              />
            }
          </Fieldset>
        ) : (
          <div key={keyString}>
            {
              <Record
                key={keyString}
                showAddButton={false}
                recordItems={record.recordItems}
                recordId={record.recordId as number}
                eformId={formId as unknown as number}
                allMatchCodes={viewModel?.matchCodes}
                efieldOptions={viewModel?.efieldOptions}
                callActionFunction={callAutoStartAction}
              />
            }
          </div>
        ),
      );
    });
    return returnValue;
  }

  function callAutoStartAction(actionId: number) {
    const actionVm = viewModel?.inivisibleHeaderActions.find(
      (a) => a.actionId === actionId,
    );

    if (actionVm) {
      if (props.isFromUrl === false) {
        actionVm.f_ROLL = true;
      }
      const actionProps = {
        action: actionVm,
        saveFunction: eformSaveFunction,
        key: actionVm.actionId,
        setActionParametersCurrentFieldsValue: setActionParamsCurrentValues,
        actionEndedFunction: actionEnded,
        navigateToInformationPanelFunction:
          props.navigateToInformationPanelFunction,
      };
      void actionVm.executeAction(
        () => setLoading(true),
        () => setLoading(false),
        actionProps,
        (navStr: string) => {
          navStr = navStr + window.location.search;
          void navigate(navStr);
        },
        accessToken,
        dispatch,
      );
    }
  }

  function getHeader(): string | undefined {
    if (viewModel !== undefined && viewModel.header !== undefined) {
      return getTranslatedStringFromParameters(viewModel.header, parameters);
    } else {
      return viewModel?.header;
    }
  }

  function handleCancelClick() {
    // Clear any error state when canceling
    dispatch(clearIsError());
    setHasDataFetchError(false);

    // Close the roll out window if this is not from URL
    if (!props.isFromUrl && props.closeFrollFunction) {
      props.closeFrollFunction(false);
    } else if (props.isFromUrl) {
      // Navigate back if opened from URL
      void navigate(-1);
    }
  }

  // Check if we should show the cancel button in footer due to errors
  const shouldShowCancelButton =
    hasDataFetchError ||
    (errorState.errorState !== ErrorState.NoError &&
      !errorState.silent &&
      (errorState.errorState === ErrorState.Forbidden ||
        errorState.errorState === ErrorState.Unauthorized ||
        errorState.errorState === ErrorState.NotFound ||
        errorState.errorState === ErrorState.InternalServerError ||
        errorState.errorState === ErrorState.OtherError));

  const isFooter =
    (footerButtons !== undefined && footerButtons.length > 0) ||
    shouldShowCancelButton;
  return (
    <div
      style={{ overflowY: "hidden", height: "100%" }}
      className="iuiBackgroundColor"
    >
      {isLoading ? (
        <div
          className="becs-ltr-margin"
          style={{ width: "100%", display: "flex", justifyContent: "center" }}
        >
          <ProgressRadial indeterminate />
        </div>
      ) : (
        <div
          style={{ height: "100%", display: "flex", flexDirection: "column" }}
        >
          {props.isFromUrl && (
            <div className="becs-all-margin">
              <div className="rowWrap">
                {getHeaderBackAction()}
                <Text as="h3" variant="title">
                  {getHeader()}
                </Text>
              </div>
            </div>
          )}
          {headerButtons !== undefined && headerButtons.length > 0 && (
            <div
              className="becs-ltr-margin"
              style={
                viewModel?.isOnlyWithTiles
                  ? {
                      height: "100%",
                      overflowY: "auto",
                      paddingBottom: "10px",
                      paddingTop: "4px",
                    }
                  : {}
              }
            >
              <div className="rowWrap">{headerButtons}</div>
            </div>
          )}
          <div
            className="becs-infopanelbody-footer-line"
            style={{
              flex: 1,
              overflowY: "auto",
            }}
          >
            <div className="becs-all-margin">{getRecords()}</div>
          </div>
          {isFooter && (
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <div className="becs-all-margin">
                <div className="rowWrap">
                  {footerButtons}
                  {shouldShowCancelButton && (
                    <Button
                      key="cancelBtn"
                      as="button"
                      onClick={handleCancelClick}
                    >
                      Cancel
                    </Button>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Eform;
